// MUI component defaults & overrides — the bulk of the "premium" feel lives
// here so individual components stay clean. All values pull from the theme
// (palette/shadows) so light & dark stay correct automatically.

// Design direction: surfaces are fully squared off. The ONLY rounded element
// is the button family, and only very slightly.
const RADIUS = 0; // cards, paper, inputs, tables, chips — square
const BUTTON_RADIUS = 4; // buttons / icon buttons / nav items — subtle rounding

const getComponents = () => ({
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      body: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      },
      // Themed scrollbars in both modes.
      "*::-webkit-scrollbar": { width: 8, height: 8 },
      "*::-webkit-scrollbar-thumb": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(219,218,222,0.24)"
            : "rgba(47,43,61,0.24)",
        borderRadius: 8,
      },
      "*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    }),
  },

  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { backgroundImage: "none" },
      rounded: { borderRadius: RADIUS },
    },
  },

  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        // Cards/badges get a subtle radius; tables stay square (see DataGrid /
        // TableContainer overrides below).
        borderRadius: BUTTON_RADIUS,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[2],
        backgroundColor: theme.palette.background.paper,
      }),
    },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: { borderRadius: BUTTON_RADIUS, paddingInline: 18, paddingBlock: 8 },
      containedPrimary: ({ theme }) => ({
        "&:hover": { backgroundColor: theme.palette.primary.dark },
      }),
    },
  },

  MuiIconButton: {
    styleOverrides: { root: { borderRadius: BUTTON_RADIUS } },
  },

  MuiChip: {
    styleOverrides: { root: { borderRadius: BUTTON_RADIUS, fontWeight: 600 } },
  },

  // One consistent pagination style app-wide (rounded, primary).
  MuiPagination: {
    defaultProps: { shape: "rounded", color: "primary" },
  },
  MuiPaginationItem: {
    styleOverrides: { root: { borderRadius: BUTTON_RADIUS, fontWeight: 600 } },
  },

  MuiTextField: {
    defaultProps: { variant: "outlined", size: "small" },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: RADIUS },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        borderRadius: BUTTON_RADIUS,
        fontSize: "0.75rem",
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[900],
      }),
    },
  },

  // Tables show subtle horizontal row divider lines (no vertical separators),
  // matching the standard DataGrid look. Header has a subtle background tint.
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({ borderBottom: `1px solid ${theme.palette.divider}` }),
      head: ({ theme }) => ({
        fontWeight: 700,
        color: theme.palette.text.secondary,
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[50],
      }),
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }),
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: { borderRadius: 0 },
    },
  },

  MuiAppBar: {
    defaultProps: { elevation: 0, color: "default" },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundImage: "none",
      }),
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundImage: "none",
      }),
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: BUTTON_RADIUS,
        marginBlock: 2,
        "&.Mui-selected": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          "&:hover": { backgroundColor: theme.palette.primary.dark },
          "& .MuiListItemIcon-root": { color: theme.palette.primary.contrastText },
        },
      }),
    },
  },

  // @mui/x-data-grid v5: square outer frame, subtle horizontal row divider
  // lines (no vertical column separators), and the default footer
  // ("Rows per page / 1-N of total / prev-next"). Dark-aware.
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: "none",
        borderRadius: 0,
        backgroundColor: theme.palette.background.paper,
        "& .MuiDataGrid-cell": { borderBottom: `1px solid ${theme.palette.divider}` },
        "& .MuiDataGrid-columnSeparator": { display: "none" },
      }),
      columnHeaders: ({ theme }) => ({
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        fontWeight: 700,
      }),
      columnHeaderTitle: { fontWeight: 700 },
      row: ({ theme }) => ({
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }),
      footerContainer: ({ theme }) => ({
        borderTop: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
});

export default getComponents;
